/**
 * Competitor domain display-name mapping and formatter.
 *
 * Shared between PriceComparisonTable (column headers) and
 * FilterPanel (competitor checkbox labels).
 *
 * Display rules for the Price Comparison tab column headers:
 *   - No spaces in domain names
 *   - All uppercase (applied via CSS ``uppercase`` class)
 *   - TLD suffixes removed entirely (no " CO", " COM", " CO UK")
 *
 * These rules apply only to browser display in the Price Comparison
 * tab; report/export data uses the full display names from the
 * backend domain registry.
 */

/**
 * Known competitor domain display names keyed by full ``domain_id``
 * as returned by the backend API (e.g. ``allbeauty_com``).
 */
export const DOMAIN_NAMES = {
  allbeauty_com: 'Allbeauty',
  // Synthetic domain: the our-price pass derived from competitor CSVs
  // (Quality tab only).
  our_store: 'Our Store (Paco)',
  beautybase_com: 'BeautyBase',
  boots_com: 'Boots',
  debenhams_com: 'Debenhams',
  jppharmacy_co_uk: 'JPPharmacy',
  justmylook_com: 'JustMyLook',
  landyschemist_com: 'LandysChemist',
  lookfantastic_com: 'LookFantastic',
  my_origines_com: 'MyOrigines',
  notino_co_uk: 'Notino',
  pacoperfumerias_co_uk: 'PacoPerfumerias',
  parfumdreams_co_uk: 'Parfumdreams',
  perfumesclub_co_uk: 'PerfumesClub',
  superdrug_com: 'Superdrug',
  thefragranceshop_co_uk: 'TheFragranceShop',
  theperfumeshop_com: 'ThePerfumeShop',
};

/**
 * Convert a domain_id to a human-readable display name.
 *
 * Looks up the full ``domain_id`` (e.g. ``allbeauty_com``) in
 * ``DOMAIN_NAMES``.  Falls back to stripping the TLD suffix and
 * title-casing the base name when the domain is not in the lookup.
 *
 * @param {string} domainId  Full domain_id from the backend.
 * @returns {string}
 */
export function domainName(domainId) {
  if (DOMAIN_NAMES[domainId]) return DOMAIN_NAMES[domainId];

  // Fallback: strip common TLD suffixes and join without spaces
  return domainId
    .replace(/_co_uk$/, '')
    .replace(/_com$/, '')
    .replace(/_uk$/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}
